import toast from 'react-hot-toast';
import { mergeMensagem } from '../../utils/chatUtils';

/**
 * O QUE O WIDGET FAZ COM CADA EVENTO DO WEBSOCKET — o `switch` inteiro, e nada mais.
 *
 * Saiu de `ChatWidget.js` (regra de 500) sem mudar uma linha. Lá ficou a TELA (o FAB, o painel,
 * o header, a conexão); aqui está o reducer: nove tipos de evento e o que cada um faz com a
 * lista de conversas, com as mensagens e com a conversa aberta.
 *
 * POR QUE ESTE FOI O CORTE. Os nove `case` só se leem juntos — três deles (`canal_criado`,
 * `canal_atualizado`, `canal_removido`) dividem os MESMOS dois refs (`canaisMutRef` e as
 * lápides de `removidosRef`), e é a coordenação entre eles que impede o "criei um grupo,
 * aparece e some". Espalhar um `case` num arquivo e outro noutro é exatamente como essa
 * coordenação se perde.
 *
 * FÁBRICA, e não hook: as dependências entram como argumento e o `ChatWidget` memoiza o
 * resultado com a MESMA lista de dependências que o `useCallback` original tinha
 * (`[meuId, marcarLido]`) — a identidade da função não muda de comportamento com o split, e
 * ela é o que `useChatSocket` recebe.
 */
export function criarOnEvento({
  canalAtivoRef, setMensagens, setCanais, setCanalAtivo,
  pendentesRef, canaisMutRef, removidosRef, meuId, marcarLido,
}) {
  return function onEvento(data, canalId) {
    switch (data.tipo) {
      case 'nova_mensagem':
        if (data.mensagem?.canal_id !== canalAtivoRef.current?.id) break;
        setMensagens(prev => mergeMensagem(prev, data.mensagem));
        if (data.mensagem?.client_msg_id) pendentesRef.current.delete(data.mensagem.client_msg_id);
        // Recebida de outro enquanto lemos: mantém o canal marcado como lido.
        if (data.mensagem?.autor_id && data.mensagem.autor_id !== meuId) marcarLido(canalId);
        break;
      case 'msg_ack':
        setMensagens(prev => prev.map(m => (m.client_msg_id && m.client_msg_id === data.client_msg_id)
          ? { ...m, id: data.mensagem_id || m.id, created_at: data.created_at || m.created_at, _status: 'enviado' }
          : m));
        pendentesRef.current.delete(data.client_msg_id);
        break;
      case 'mensagem_editada':
        setMensagens(prev => prev.map(m => m.id === data.mensagem.id ? data.mensagem : m));
        break;
      case 'mensagem_deletada':
        setMensagens(prev => prev.map(m => m.id === data.mensagem_id ? { ...m, deletado: true, texto: '', anexo: null } : m));
        break;
      case 'reacao_atualizada':
        setMensagens(prev => prev.map(m => m.id === data.mensagem_id ? { ...m, reacoes: data.reacoes } : m));
        break;
      case 'canal_criado':
        // Fui incluído numa conversa agora — ela entra na lista sem recarregar a página.
        // O evento vem por PESSOA (broadcast_to_user), não pelo canal: eu não tenho socket
        // num canal que acabei de descobrir. Aqui ele chega pelo socket do canal ABERTO, que
        // é o único que o widget mantém (ver o efeito de conexão) — logo só alcança quem está
        // com o painel aberto DENTRO de uma conversa. Fora disso quem cobre é o poll de 30s.
        // Não duplica: o canal já pode estar na lista se o refetch tiver cruzado com o evento.
        // É também o evento de RESTAURAÇÃO: a lápide sai aqui, senão o canal restaurado
        // continuaria filtrado até um GET mais novo que ela.
        if (data.canal?.id) {
          canaisMutRef.current++;
          removidosRef.current.delete(data.canal.id);
          setCanais(prev => (prev.some(c => c.id === data.canal.id)
            ? prev.map(c => c.id === data.canal.id ? { ...c, ...data.canal } : c)
            : [data.canal, ...prev]));
        }
        break;
      case 'canal_atualizado':
        // Renome/foto: o widget MOSTRA nome e foto do canal (header da conversa + lista),
        // então sem isto o header fica com o nome velho até fechar e reabrir. Alcança o
        // widget em dois casos: PATCH /canais (broadcast_to_canal → só o canal ativo, que é
        // justamente o do header) e apelido privado de direto (broadcast_to_user → minhas
        // outras abas). canalAtivo é um objeto à parte da lista: precisa do mesmo merge.
        if (data.canal?.id) {
          canaisMutRef.current++;
          setCanais(prev => prev.map(c => c.id === data.canal.id ? { ...c, ...data.canal } : c));
          setCanalAtivo(prev => (prev && prev.id === data.canal.id ? { ...prev, ...data.canal } : prev));
        }
        break;
      case 'canal_removido': {
        // O id vem NO EVENTO, nunca da closure: ele chega por PESSOA, então pode entrar pelo
        // socket de OUTRA conversa — usar o canal do socket fecharia a errada.
        const removido = data.canal_id;
        if (!removido) break;
        canaisMutRef.current++;
        removidosRef.current.set(removido, Date.now());
        setCanais(prev => prev.filter(c => c.id !== removido));
        setCanalAtivo(prev => {
          if (!prev || prev.id !== removido) return prev;
          setMensagens([]);
          toast.error('Esta conversa foi arquivada.');
          return null;      // o painel volta para a lista; sem isso ficaria uma conversa fantasma
        });
        break;
      }
      case 'erro':
        if (data.detail) toast.error(data.detail);
        if (data.client_msg_id) {
          pendentesRef.current.delete(data.client_msg_id);
          setMensagens(prev => prev.map(m => m.client_msg_id === data.client_msg_id ? { ...m, _status: 'falhou' } : m));
        }
        break;
      default: break;
    }
  };
}
